/**
 * Storage service — URL resolution (with caching) and file uploads.
 * Callers never reference bucket names or paths directly; all logic lives in modules/storage.ts.
 */

import { useState, useEffect, useMemo } from 'react'
import type { SupabaseClient } from '@supabase/supabase-js'
import * as core from '#modules/storage'

// ── Resolution ───────────────────────────────────────────────────────────────

type CacheEntry = { url: string; expiresAt: number }
const urlCache = new Map<string, CacheEntry>()
const SIGNED_URL_TTL = 3600
const REFRESH_BUFFER_MS = 5 * 60 * 1000

/** Resolve a stored value to a usable URL, with in-memory caching. */
export async function resolveUrl(supabase: SupabaseClient, stored: string | null | undefined, expiresIn = SIGNED_URL_TTL): Promise<string | undefined> {
  if (!stored) return undefined
  const cached = urlCache.get(stored)
  if (cached && cached.expiresAt > Date.now() + REFRESH_BUFFER_MS) return cached.url
  const url = await core.resolveUrl(supabase, stored, expiresIn)
  if (url) urlCache.set(stored, { url, expiresAt: Date.now() + expiresIn * 1000 })
  return url
}

/** Hook: resolve a single stored value to a usable URL. */
export function useSignedUrl(supabase: SupabaseClient, stored: string | null | undefined): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined)
  useEffect(() => {
    let cancelled = false
    resolveUrl(supabase, stored).then(r => { if (!cancelled) setUrl(r) })
    return () => { cancelled = true }
  }, [supabase, stored])
  return url
}

/** Hook: batch-resolve stored values to usable URLs.
 *  Groups by bucket and calls createSignedUrls once per bucket.
 *  Use in list views instead of per-item useSignedUrl.
 */
export function useSignedUrls(
  supabase: SupabaseClient,
  stored: (string | null | undefined)[]
): Map<string, string> {
  const [urls, setUrls] = useState<Map<string, string>>(new Map())
  const key = useMemo(() => stored.filter(Boolean).sort().join('\0'), [stored])

  useEffect(() => {
    let cancelled = false
    const cached = new Map<string, string>()
    const uncached: string[] = []

    for (const s of stored) {
      if (!s) continue
      const hit = urlCache.get(s)
      if (hit && hit.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
        cached.set(s, hit.url)
      } else {
        uncached.push(s)
      }
    }

    if (uncached.length === 0) {
      setUrls(cached)
      return
    }

    core.resolveUrls(supabase, uncached).then(resolved => {
      if (cancelled) return
      for (const [s, url] of resolved) {
        cached.set(s, url)
        urlCache.set(s, { url, expiresAt: Date.now() + SIGNED_URL_TTL * 1000 })
      }
      setUrls(new Map(cached))
    })

    return () => { cancelled = true }
  }, [supabase, key])

  return urls
}

// ── Uploads ──────────────────────────────────────────────────────────────────

export function uploadAmaPdf(supabase: SupabaseClient, encounterUuid: string | null, consentId: string, data: Blob | ArrayBufferView): Promise<string> {
  return core.uploadAmaPdf(supabase, encounterUuid, consentId, data)
}

export function uploadCatalogImage(supabase: SupabaseClient, catalogItemId: string, file: File): Promise<string> {
  return core.uploadCatalogImage(supabase, catalogItemId, file, file.type)
}

export function uploadChatAttachment(supabase: SupabaseClient, channelId: string, file: File): Promise<string> {
  return core.uploadChatAttachment(supabase, channelId, file, file.type)
}

export function uploadCompClaimPdf(supabase: SupabaseClient, encounterUuid: string | null, claimId: string, data: Blob | ArrayBufferView): Promise<string> {
  return core.uploadCompClaimPdf(supabase, encounterUuid, claimId, data)
}

export function uploadConsentSignature(supabase: SupabaseClient, incidentId: string | null, consentId: string, data: Blob | ArrayBufferView, contentType = 'image/png'): Promise<string> {
  return core.uploadConsentSignature(supabase, incidentId, consentId, data, contentType)
}

export function uploadAMASignature(supabase: SupabaseClient, incidentId: string | null, consentId: string, role: 'patient' | 'provider', data: Blob | ArrayBufferView, contentType = 'image/png'): Promise<string> {
  return core.uploadAMASignature(supabase, incidentId, consentId, role, data, contentType)
}

export function uploadConsentToTreatPdf(supabase: SupabaseClient, encounterUuid: string | null, consentId: string, data: Blob | ArrayBufferView): Promise<string> {
  return core.uploadConsentToTreatPdf(supabase, encounterUuid, consentId, data)
}

export function uploadCredential(supabase: SupabaseClient, employeeId: string, file: File, canonicalName: string, contentType = file.type): Promise<string> {
  return core.uploadCredential(supabase, employeeId, canonicalName, file, contentType)
}

export function uploadEmployeeHeadshot(supabase: SupabaseClient, employeeId: string, file: File): Promise<string> {
  return core.uploadEmployeeHeadshot(supabase, employeeId, file, file.type)
}

export function uploadEncounterPhoto(supabase: SupabaseClient, encounterUuid: string, file: File): Promise<string> {
  return core.uploadEncounterPhoto(supabase, encounterUuid, file, file.type)
}

export function uploadExpenseReceipt(supabase: SupabaseClient, incidentId: string, file: File): Promise<string> {
  return core.uploadExpenseReceipt(supabase, incidentId, file, file.type)
}

export function uploadICS214Pdf(supabase: SupabaseClient, incidentId: string, ics214Id: string, data: Blob | ArrayBufferView): Promise<string> {
  return core.uploadICS214Pdf(supabase, incidentId, ics214Id, data)
}

export function uploadICS214Signature(supabase: SupabaseClient, incidentId: string, ics214Id: string, data: Blob | ArrayBufferView, contentType = 'image/png'): Promise<string> {
  return core.uploadICS214Signature(supabase, incidentId, ics214Id, data, contentType)
}

export function uploadIncidentContract(supabase: SupabaseClient, incidentId: string, file: File): Promise<string> {
  return core.uploadIncidentContract(supabase, incidentId, file, file.type)
}

export function uploadNemsisXml(supabase: SupabaseClient, encounterUuid: string, data: Blob | ArrayBufferView): Promise<string> {
  return core.uploadNemsisXml(supabase, encounterUuid, data)
}

export function uploadOrgBackground(supabase: SupabaseClient, file: File): Promise<string> {
  return core.uploadOrgBackground(supabase, file, file.type)
}

export function uploadOrgDocument(supabase: SupabaseClient, file: File): Promise<string> {
  return core.uploadOrgDocument(supabase, file, file.type)
}

export function uploadOrgLogo(supabase: SupabaseClient, file: File): Promise<string> {
  return core.uploadOrgLogo(supabase, file, file.type)
}

export function uploadUnitPhoto(supabase: SupabaseClient, unitId: string, file: File): Promise<string> {
  return core.uploadUnitPhoto(supabase, unitId, file, file.type)
}

export function uploadVehicleDocument(supabase: SupabaseClient, unitId: string, file: File): Promise<string> {
  return core.uploadVehicleDocument(supabase, unitId, file, file.type)
}
